import {HalContext} from "@cm/hal/hal-context"
import {HalPainter} from "@cm/hal/hal-painter/index"
import {HalPainterSourceImage, HalPainterTarget} from "@cm/hal/hal-painter-primitive/index"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {HalPainterImageBlitOptions} from "@cm/hal"
import {HalPainterImageBlitRef} from "@cm/hal/hal-ref"

export class HalPainterImageStretch implements HalPainter {
    constructor(
        readonly context: HalContext,
        transparencyGrid = false,
    ) {
        this.blit = this.context.createHalPainterImageBlit(compositingFunction(transparencyGrid))
    }

    dispose(): void {
        if (!this.blit) {
            throw new Error("HalPainterImageStretch: Already disposed")
        }
        this.blit.release()
        this.blit = null
    }

    paint(target: HalPainterTarget, sourceImage: HalPainterSourceImage, options?: HalPainterImageBlitOptions) {
        if (!this.blit) {
            throw new Error("HalPainterImageStretch: Already disposed")
        }
        this.blit.ref.paint({
            target,
            sourceImages: sourceImage,
            options,
        })
    }

    public static create(context: HalContext, transparencyGrid = false): HalPainterImageStretch {
        return new HalPainterImageStretch(context, transparencyGrid)
    }

    private blit: HalPainterImageBlitRef | null
}

// The grid is laid out in target pixels (gl_FragCoord), so it keeps its size while the image is zoomed; the image is premultiplied.
const compositingFunction = (transparencyGrid: boolean) => `
    vec4 computeColor(ivec2 targetPixel, ivec2 sourcePixel, int lod, int addressMode) {
        ${
            TextureEditorSettings.InterpolateStretchBlit
                ? `
        float mipLevelScale = 1.0 / float(1 << lod);
        vec4 color = textureLodPx0((vec2(targetPixel) + 0.5) * mipLevelScale, lod, addressMode);
        `
                : `
        vec4 color = texelFetchLod0(targetPixel >> lod, lod, addressMode);
        `
        }
        ${
            transparencyGrid
                ? `
        ivec2 cell = ivec2(gl_FragCoord.xy) / 16;
        float grid = (cell.x + cell.y) % 2 == 0 ? 1.0 : 0.8;
        return vec4(color.rgb + (1.0 - color.a) * grid, 1.0);
        `
                : `
        return color;
        `
        }
    }
`
